import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme } from '@mui/material'
import { useAsyncEffect } from '@xylabs/sdk-react'
import { Quadkey } from '@xyo-network/sdk-xyoworld-js'
import BigNumber from 'bn.js'
import { useContext, useState } from 'react'

import { CurrencyBalanceContext, useAuction } from '../Contexts'
import { shiftBase10 } from '../shiftBase10'
import XyoTextField from './XyoTextField'

interface MintNowDialogProps {
  onClose?: () => void
  open?: boolean
  quadkey?: Quadkey
}

const MintNowDialogOpen: React.FC<MintNowDialogProps> = (props) => {
  const { quadkey, onClose, open = false } = props
  const { balance, minterAllowance, refresh } = useContext(CurrencyBalanceContext)
  const { auction } = useAuction(quadkey)
  const theme = useTheme()
  const [mintingPrice, setMintingPrice] = useState<BigNumber>()

  const [startingBid, setStartingBid] = useState<BigNumber>()
  const [currentBid, setCurrentBid] = useState<BigNumber>()

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      const hasBid = await auction?.fetchHasBid()
      const currentBid = hasBid ? await auction?.getCurrentBid() : undefined
      const startingBid = hasBid ? undefined : await auction?.getCurrentBid()
      if (isMounted()) {
        setCurrentBid(currentBid)
        setStartingBid(startingBid)
      }
    },
    [auction],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      const mintNowPrice = await auction?.getMintNowPrice()
      if (isMounted()) {
        setMintingPrice(mintNowPrice)
      }
    },
    [auction],
  )

  const onMintClicked = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    await auction?.actions.mint()
    onClose?.()
    refresh?.()
  }

  if (auction) {
    const allowanceTooLow = minterAllowance?.lt(mintingPrice ?? new BigNumber(0))
    const balanceTooLow = balance?.lt(mintingPrice ?? new BigNumber(0))

    return (
      <Dialog
        onClose={() => {
          onClose?.()
        }}
        open={open}
      >
        <DialogTitle>Mint Now</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column">
            {startingBid ? (
              <Box display="flex" justifyContent="space-between" padding={1}>
                <XyoTextField fullWidth label="Starting Bid" value={shiftBase10(startingBid, -18)?.toString() ?? '0'} variant="outlined" />
              </Box>
            ) : null}
            {currentBid ? (
              <Box display="flex" justifyContent="space-between" padding={1}>
                <XyoTextField fullWidth label="Current Bid" value={shiftBase10(currentBid, -18)?.toString() ?? '0'} variant="outlined" />
              </Box>
            ) : null}
            <Box display="flex" justifyContent="space-between" padding={1}>
              <XyoTextField
                fullWidth
                label="Minting Deposit"
                readOnly={true}
                value={shiftBase10(mintingPrice, -18)?.toString() ?? '0'}
                variant="outlined"
              />
            </Box>
          </Box>
          <Box color={theme.palette.warning.main} display={allowanceTooLow ? 'flex' : 'none'} justifyContent="center">
            <Typography align="center">Your XYO allowance is too low.</Typography>
          </Box>
          <Box color={theme.palette.warning.main} display={balanceTooLow ? 'flex' : 'none'} justifyContent="center">
            <Typography align="center">Your XYO balance is too low.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" width="100%">
            <Box>
              <Button
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  event.stopPropagation()
                  onClose?.()
                }}
                variant="text"
              >
                Close
              </Button>
            </Box>
            <Box flexGrow={1}></Box>
            <Box>
              <Button disabled={balanceTooLow || allowanceTooLow} onClick={onMintClicked} variant="text">
                Mint
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    )
  }
  return null
}

const MintNowDialog: React.FC<MintNowDialogProps> = (props) => {
  const { open } = props
  if (open) {
    return <MintNowDialogOpen {...props} />
  } else {
    return null
  }
}

export default MintNowDialog
